import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';

import { setPageTitle } from '../../redux/title/title.slice';
import { logout } from '../../redux/auth/auth.slice';

const ORANGE = 'rgb(183, 90, 32)';
const DARK = '#202020';

const MyProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    dispatch(setPageTitle('My Profile'));
  }, [dispatch]);

  const firstName = user?.given_name || '';
  const lastName = user?.family_name || '';
  const email = user?.email || '';
  const phone = user?.phone_number || '';

  const fullName = `${firstName} ${lastName}`.trim() || 'User';

  const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase() || 'U';

  const handleLogout = async () => {
    try {
      const endSessionUrl = await dispatch(logout()).unwrap();

      if (endSessionUrl) {
        window.location.assign(endSessionUrl);
      } else {
        navigate('/', { replace: true });
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 900,
        mx: 'auto',
        py: { xs: 2, md: 5 }
      }}>
      {/* ================================================== */}
      {/* Profile Header */}
      {/* ================================================== */}

      <Card
        elevation={0}
        sx={{
          overflow: 'hidden',
          border: '1px solid #E7E7E7',
          backgroundColor: '#FFFFFF',
          mb: 3
        }}>
        {/* Accent strip */}
        <Box
          sx={{
            height: 8,
            backgroundColor: ORANGE
          }}
        />

        <CardContent
          sx={{
            p: { xs: 3, md: 4 }
          }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            alignItems={{ xs: 'flex-start', sm: 'center' }}>
            {/* Avatar */}
            <Avatar
              sx={{
                width: 76,
                height: 76,
                backgroundColor: ORANGE,
                color: '#FFFFFF',
                fontSize: 25,
                fontWeight: 600
              }}>
              {initials}
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: DARK,
                  mb: 0.5
                }}>
                {fullName}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary'
                }}>
                Manage your account information and preferences
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* ================================================== */}
      {/* Personal Details */}
      {/* ================================================== */}

      <Card
        elevation={0}
        sx={{
          border: '1px solid #E7E7E7',
          backgroundColor: '#FFFFFF',
          mb: 3
        }}>
        <CardContent
          sx={{
            p: { xs: 3, md: 4 }
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <PersonOutlineIcon
              sx={{
                color: ORANGE,
                fontSize: 23
              }}
            />

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: DARK
              }}>
              Personal Details
            </Typography>
          </Stack>

          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 3
            }}>
            Your account information is shown below.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: '1fr 1fr'
              },
              gap: 2.5
            }}>
            <TextField
              label="First Name"
              value={firstName}
              fullWidth
              disabled
              InputProps={{
                startAdornment: (
                  <PersonOutlineIcon
                    sx={{
                      mr: 1,
                      color: 'text.secondary'
                    }}
                  />
                )
              }}
            />

            <TextField
              label="Last Name"
              value={lastName}
              fullWidth
              disabled
              InputProps={{
                startAdornment: (
                  <PersonOutlineIcon
                    sx={{
                      mr: 1,
                      color: 'text.secondary'
                    }}
                  />
                )
              }}
            />

            <TextField
              label="Email Address"
              value={email}
              fullWidth
              disabled
              InputProps={{
                startAdornment: (
                  <EmailOutlinedIcon
                    sx={{
                      mr: 1,
                      color: 'text.secondary'
                    }}
                  />
                )
              }}
            />

            <TextField
              label="Phone Number"
              value={phone}
              fullWidth
              disabled
              InputProps={{
                startAdornment: (
                  <PhoneOutlinedIcon
                    sx={{
                      mr: 1,
                      color: 'text.secondary'
                    }}
                  />
                )
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* ================================================== */}
      {/* Account Actions */}
      {/* ================================================== */}

      <Card
        elevation={0}
        sx={{
          border: '1px solid #E7E7E7',
          backgroundColor: '#FFFFFF'
        }}>
        <CardContent
          sx={{
            p: { xs: 3, md: 4 }
          }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: DARK,
              mb: 0.5
            }}>
            Account Settings
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 3
            }}>
            Manage your account and security settings.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Stack spacing={1.5}>
            {/* Reset Password */}
            <Button
              variant="outlined"
              startIcon={<LockResetOutlinedIcon />}
              href="https://api.dataviewfund.com/password/forgot?tenant=fairtree"
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                borderColor: '#D8D8D8',
                color: DARK,
                py: 1.3,
                px: 2,

                '&:hover': {
                  borderColor: ORANGE,
                  color: ORANGE,
                  backgroundColor: 'rgba(183, 90, 32, 0.04)'
                }
              }}>
              Reset Password
            </Button>

            {/* Update Email */}
            <Button
              variant="outlined"
              disabled
              startIcon={<EmailOutlinedIcon />}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                py: 1.3,
                px: 2
              }}>
              Update Email Address
            </Button>

            {/* Update Details */}
            <Button
              variant="outlined"
              disabled
              startIcon={<EditOutlinedIcon />}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                py: 1.3,
                px: 2
              }}>
              Update Details
            </Button>

            <Divider sx={{ my: 1 }} />

            {/* Logout */}
            <Button
              variant="outlined"
              startIcon={<LogoutOutlinedIcon />}
              onClick={handleLogout}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                py: 1.3,
                px: 2,

                borderColor: '#D8D8D8',
                color: '#333333',

                '&:hover': {
                  borderColor: ORANGE,
                  color: ORANGE,
                  backgroundColor: 'rgba(183, 90, 32, 0.04)'
                }
              }}>
              Log Out
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MyProfile;
